import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Firestore, collection, getDocs } from '@angular/fire/firestore';
import { Observable, from, BehaviorSubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { WorkCurrentStatus, WorkshopWork } from '../core/models';
import { AuthService } from '../core/auth.service';

interface WorkCounts {
  [status: string]: number;
}

interface DashboardStats {
  week: WorkCounts;
  month: WorkCounts;
  year: WorkCounts;
  total: WorkCounts;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit {
  stats$: Observable<DashboardStats | null>;
  isLoading = true;
  
  // Control de la pestaña activa (Semana, Mes, Año)
  selectedPeriod: 'week' | 'month' | 'year' = 'week';

  statusOrder: WorkCurrentStatus[] = [
    'Received',
    'In Progress',
    'Awaiting Parts',
    'Ready',
    'Handed Off',
    'Completed',
  ];

  constructor(
    private firestore: Firestore,
    private router: Router,
    public authService: AuthService // Inyectado para personalizar el saludo
  ) {
    // Hacemos un solo fetch a la base de datos y lo dividimos localmente
    const worksRef = collection(this.firestore, 'workshop_works');
    this.stats$ = from(getDocs(worksRef)).pipe(
      map(querySnapshot => {
        const works = querySnapshot.docs.map(doc => doc.data() as WorkshopWork);
        return this.calculateAdvancedStats(works);
      }),
      tap(() => this.isLoading = false),
      catchError(err => {
        console.error('[HomeComponent] Error obteniendo la data:', err);
        this.isLoading = false;
        return from([null]);
      })
    );
  }

  ngOnInit(): void {}

  private calculateAdvancedStats(works: WorkshopWork[]): DashboardStats {
    const stats: DashboardStats = {
      week: this.getEmptyCounts(),
      month: this.getEmptyCounts(),
      year: this.getEmptyCounts(),
      total: this.getEmptyCounts()
    };

    const now = new Date();
    
    // Límites de tiempo
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Domingo
    startOfWeek.setHours(0, 0, 0, 0);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    works.forEach(work => {
      const status = work.currentStatus;
      if (!status || !stats.total.hasOwnProperty(status)) return;

      const entryDate = work.entryDate?.toDate ? work.entryDate.toDate() : new Date(work.entryDate);

      // Sumar al total histórico
      stats.total[status]++;

      // Sumar al Año
      if (entryDate >= startOfYear) stats.year[status]++;
      
      // Sumar al Mes
      if (entryDate >= startOfMonth) stats.month[status]++;
      
      // Sumar a la Semana
      if (entryDate >= startOfWeek) stats.week[status]++;
    });

    return stats;
  }

  private getEmptyCounts(): WorkCounts {
    return {
      'Received': 0, 'In Progress': 0, 'Awaiting Parts': 0, 
      'Ready': 0, 'Handed Off': 0, 'Completed': 0, 'Cancelled': 0
    };
  }

  setPeriod(period: 'week' | 'month' | 'year'): void {
    this.selectedPeriod = period;
  }

  getSpanishStatus(statusValue: string): string {
    const statusMap: { [key: string]: string } = {
      'Received': 'Recibido',
      'In Progress': 'En Progreso',
      'Awaiting Parts': 'En Pausa (Piezas)',
      'Ready': 'Listo para Entrega',
      'Handed Off': 'Entregado',
      'Completed': 'Completado',
      'Cancelled': 'Cancelado'
    };
    return statusMap[statusValue] || statusValue;
  }

  getStatusIcon(status: string): string {
    const icons: Record<string, string> = {
      'Received': 'bi-inbox-fill',
      'In Progress': 'bi-tools',
      'Awaiting Parts': 'bi-hourglass-split',
      'Ready': 'bi-check-circle-fill',
      'Handed Off': 'bi-car-front-fill',
      'Completed': 'bi-flag-fill'
    };
    return icons[status] || 'bi-circle';
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      'Received': 'text-primary',
      'In Progress': 'text-info',
      'Awaiting Parts': 'text-warning',
      'Ready': 'text-success',
      'Handed Off': 'text-teal',
      'Completed': 'text-dark'
    };
    return colors[status] || 'text-secondary';
  }

  navigateToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }
}